import { useState, useEffect } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Youtube, Instagram } from "lucide-react";
import { SOCIAL_LINKS, IMAGES, TAGLINE } from "@/data/content";

const navItems = [
  { to: "/", label: "home", testid: "nav-home" },
  { to: "/videos", label: "videos", testid: "nav-videos" },
  { to: "/audio", label: "audio", testid: "nav-audio" },
  { to: "/merch", label: "merch", testid: "nav-merch" },
  { to: "/apply", label: "apply", testid: "nav-apply" },
  { to: "/voicemail", label: "voicemail", testid: "nav-voicemail" },
  { to: "/suggest", label: "ideas", testid: "nav-suggest" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 bg-[var(--tsws-orange)] border-b-2 border-[var(--tsws-green)]"
    >
      <div className="flex items-center justify-between px-6 lg:px-12 h-20">
        <Link
          to="/"
          data-testid="brand-logo"
          className="flex items-center gap-3 group"
          onClick={() => setOpen(false)}
        >
          <img
            src={IMAGES.logo}
            alt="The Sesh With Sav"
            className="h-12 w-12 object-cover border-2 border-[var(--tsws-green)] print-roughen group-hover:rotate-[-6deg] transition-transform"
          />
          <span className="font-display text-2xl lg:text-3xl text-[var(--tsws-green)] leading-none">
            the sesh<span className="text-[var(--tsws-cigar)]">.</span>
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              end={item.to === "/"}
              className={({ isActive }) =>
                `font-display text-base px-3 py-2 border-2 transition-colors ${
                  isActive
                    ? "border-[var(--tsws-green)] bg-[var(--tsws-green)] text-[var(--tsws-orange)]"
                    : "border-transparent text-[var(--tsws-green)] hover:border-[var(--tsws-green)]"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <a
            href={SOCIAL_LINKS.youtube}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-yt-link"
            className="ml-3 p-2 border-2 border-[var(--tsws-green)] text-[var(--tsws-green)] hover:bg-[var(--tsws-green)] hover:text-[var(--tsws-orange)] transition-colors"
            aria-label="YouTube"
          >
            <Youtube size={18} strokeWidth={2.5} />
          </a>
        </nav>

        {/* Mobile toggle — "menu" / "close" in display font, stamped like the logo */}
        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden px-4 py-2 border-2 border-[var(--tsws-green)] bg-[var(--tsws-green)] text-[var(--tsws-orange)] font-display text-2xl leading-none stamped ink-bleed"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          aria-expanded={open}
        >
          {open ? "close" : "menu"}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <nav
          data-testid="mobile-menu"
          className="lg:hidden flex flex-col border-t-2 border-[var(--tsws-green)] bg-[var(--tsws-orange)]"
        >
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`${item.testid}-mobile`}
              end={item.to === "/"}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `font-display text-3xl px-6 py-5 border-b border-[var(--tsws-green)]/30 ${
                  isActive ? "text-[var(--tsws-cigar)]" : "text-[var(--tsws-green)]"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
      )}
      <span className="sr-only">{location.pathname}</span>
    </header>
  );
};

export const Footer = () => (
  <footer
    data-testid="site-footer"
    className="relative mt-32 border-t-2 border-[var(--tsws-green)] bg-[var(--tsws-orange-deep)]"
  >
    {/* halftone band */}
    <div className="halftone h-6 opacity-40" />

    <div className="px-6 lg:px-12 py-16 grid md:grid-cols-3 gap-12 text-[var(--tsws-green)]">
      <div>
        <div className="flex items-center gap-3 mb-4">
          <img
            src={IMAGES.logo}
            alt="The Sesh"
            className="h-16 w-16 object-cover border-2 border-[var(--tsws-green)]"
          />
          <div className="font-display text-4xl leading-none">
            the sesh<span className="text-[var(--tsws-cigar)]">.</span>
          </div>
        </div>
        <p className="font-tag text-xl mt-2 mb-3">{TAGLINE}</p>
        <p className="leading-relaxed opacity-80 max-w-sm">
          Weekly chats with rappers, fighters, comedians and Aussie icons. Listen responsibly.
        </p>
      </div>
      <div>
        <div className="font-label text-sm mb-4 opacity-70">Listen / Watch</div>
        <ul className="space-y-2 font-tag text-lg">
          <li>
            <a
              href={SOCIAL_LINKS.youtube}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-youtube"
              className="hover:text-[var(--tsws-cigar)] transition-colors"
            >
              → YouTube
            </a>
          </li>
          <li>
            <a
              href={SOCIAL_LINKS.apple}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-apple"
              className="hover:text-[var(--tsws-cigar)] transition-colors"
            >
              → Apple Podcasts
            </a>
          </li>
          <li>
            <a
              href={SOCIAL_LINKS.spotify}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-spotify"
              className="hover:text-[var(--tsws-cigar)] transition-colors"
            >
              → Spotify
            </a>
          </li>
          <li>
            <a
              href={SOCIAL_LINKS.merch}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-merch-ext"
              className="hover:text-[var(--tsws-cigar)] transition-colors"
            >
              → Big Cartel Store
            </a>
          </li>
        </ul>
      </div>
      <div>
        <div className="font-label text-sm mb-4 opacity-70">Get on the show</div>
        <p className="mb-4 leading-relaxed">Reckon you'd be a sick guest? Fill out the form.</p>
        <Link
          to="/apply"
          data-testid="footer-apply-cta"
          className="inline-block font-display text-xl px-6 py-3 border-2 border-[var(--tsws-green)] bg-[var(--tsws-green)] text-[var(--tsws-orange)] hover-brutal hover:bg-[var(--tsws-cigar)] hover:border-[var(--tsws-cigar)]"
        >
          apply as guest →
        </Link>
      </div>
    </div>
    <div className="border-t-2 border-[var(--tsws-green)] px-6 lg:px-12 py-5 flex flex-col md:flex-row justify-between items-center gap-3 text-sm text-[var(--tsws-green)]">
      <span className="font-tag">
        © {new Date().getFullYear()} The Sesh With Sav. Listen responsibly.
      </span>
      <div className="flex items-center gap-4">
        <a
          href={SOCIAL_LINKS.instagram}
          target="_blank"
          rel="noreferrer"
          aria-label="Instagram"
          className="hover:text-[var(--tsws-cigar)]"
        >
          <Instagram size={18} />
        </a>
        <a
          href={SOCIAL_LINKS.youtube}
          target="_blank"
          rel="noreferrer"
          aria-label="YouTube"
          className="hover:text-[var(--tsws-cigar)]"
        >
          <Youtube size={18} />
        </a>
      </div>
    </div>
  </footer>
);

export default function Layout() {
  const { pathname, hash } = useLocation();

  // Reset scroll position on every route change so users always land at the top
  // (unless the URL has an explicit #hash anchor to scroll to).
  useEffect(() => {
    if (hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname, hash]);

  return (
    <div className="tsws min-h-screen bg-[var(--tsws-orange)] text-[var(--tsws-green)] grain">
      {/* Riso/stamp SVG filters — used by .stamped utility class */}
      <svg
        width="0"
        height="0"
        style={{ position: "absolute" }}
        aria-hidden="true"
        focusable="false"
      >
        <defs>
          <filter id="tsws-stamp" x="-5%" y="-5%" width="110%" height="110%">
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.85"
              numOctaves="2"
              seed="7"
              result="noise"
            />
            <feColorMatrix
              in="noise"
              type="matrix"
              values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 -1.6 1.3"
              result="alpha"
            />
            <feComposite in="SourceGraphic" in2="alpha" operator="in" />
          </filter>
          <filter id="tsws-stamp-heavy" x="-5%" y="-5%" width="110%" height="110%">
            <feTurbulence
              type="fractalNoise"
              baseFrequency="1.4"
              numOctaves="2"
              seed="3"
              result="noise"
            />
            <feColorMatrix
              in="noise"
              type="matrix"
              values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 -1.2 1.1"
              result="alpha"
            />
            <feComposite in="SourceGraphic" in2="alpha" operator="in" />
          </filter>
        </defs>
      </svg>

      <Header />
      <main>
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}
